class PerformanceTracker {
    constructor(options = {}){
        this.isActive = false;
        this.startTimestamp = 0;
        this.lastUpdateTimestamp = 0;
        this.activeMeasurements = new Map();
        this.completedMeasurements = [];
        this.measurementCounter = 0;
        this.options = {
            enabled: true,
            trackJavaScript: true,
            trackMemory: true,
            trackRendering: true,
            trackBlocking: true,
            collectInterval: 1000,
            maxMeasurements: 1000,
            enableLogging: false,
            onMetricsUpdate: ()=>{},
            ...options
        };
        this.metrics = this.createInitialMetrics();
        this.setupPerformanceObservers();
    }
    createInitialMetrics() {
        const now = performance.now();
        return {
            startTime: now,
            lastUpdated: now,
            totalTime: 0,
            javascript: {
                totalExecutionTime: 0,
                syncTime: 0,
                asyncWaitTime: 0,
                functionCallCount: 0,
                averageFunctionTime: 0,
                slowestFunctionTime: 0,
                slowestFunctionName: void 0
            },
            memory: {
                currentUsage: this.getCurrentMemoryUsage(),
                peakUsage: this.getCurrentMemoryUsage(),
                initialUsage: this.getCurrentMemoryUsage(),
                memoryGrowth: 0,
                jsHeapSize: this.getJSHeapSize()?.total,
                usedJSHeapSize: this.getJSHeapSize()?.used,
                totalJSHeapSizeLimit: this.getJSHeapSize()?.limit
            },
            rendering: {
                fps: 0,
                avgFps: 0,
                minFps: 1 / 0,
                maxFps: 0,
                frameCount: 0,
                avgFrameTime: 0,
                droppedFrames: 0
            },
            blocking: {
                totalBlockingTime: 0,
                longestBlockingTime: 0,
                blockingEventCount: 0,
                avgBlockingTime: 0,
                longTaskTime: 0,
                longTaskCount: 0
            },
            custom: {}
        };
    }
    setupPerformanceObservers() {
        if ('undefined' != typeof PerformanceObserver) try {
            if (this.options.trackBlocking) {
                this.performanceObserver = new PerformanceObserver((list)=>{
                    for (const entry of list.getEntries())this.handleLongTaskEntry(entry);
                });
                this.performanceObserver.observe({
                    entryTypes: [
                        'longtask'
                    ]
                });
            }
        } catch (error) {
            if (this.options.enableLogging) console.warn('PerformanceObserver not fully supported:', error);
        }
    }
    handleLongTaskEntry(entry) {
        const duration = entry.duration;
        this.metrics.blocking.totalBlockingTime += duration;
        this.metrics.blocking.longTaskTime += duration;
        this.metrics.blocking.longTaskCount++;
        this.metrics.blocking.blockingEventCount++;
        if (duration > this.metrics.blocking.longestBlockingTime) this.metrics.blocking.longestBlockingTime = duration;
        this.metrics.blocking.avgBlockingTime = this.metrics.blocking.blockingEventCount > 0 ? this.metrics.blocking.totalBlockingTime / this.metrics.blocking.blockingEventCount : 0;
    }
    start() {
        if (this.isActive || !this.options.enabled) return;
        this.isActive = true;
        this.startTimestamp = performance.now();
        this.lastUpdateTimestamp = this.startTimestamp;
        this.metrics = this.createInitialMetrics();
        if (this.options.collectInterval > 0) this.metricsCollectionInterval = window.setInterval(()=>{
            this.collectMetrics();
        }, this.options.collectInterval);
        if (this.options.trackRendering) this.startFPSTracking();
        if (this.options.enableLogging) console.log('PerformanceTracker started');
    }
    stop() {
        if (!this.isActive) return;
        this.isActive = false;
        if (this.metricsCollectionInterval) {
            clearInterval(this.metricsCollectionInterval);
            this.metricsCollectionInterval = void 0;
        }
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
            this.frameId = void 0;
        }
        this.collectMetrics();
        if (this.options.enableLogging) console.log('PerformanceTracker stopped');
    }
    isTracking() {
        return this.isActive;
    }
    collectMetrics() {
        const now = performance.now();
        this.metrics.totalTime = now - this.startTimestamp;
        this.metrics.lastUpdated = now;
        if (this.options.trackMemory) this.updateMemoryMetrics();
        if (this.options.trackJavaScript) this.updateJavaScriptMetrics();
        this.options.onMetricsUpdate(this.metrics);
    }
    updateMemoryMetrics() {
        const currentUsage = this.getCurrentMemoryUsage();
        const jsHeap = this.getJSHeapSize();
        this.metrics.memory.currentUsage = currentUsage;
        if (currentUsage > this.metrics.memory.peakUsage) this.metrics.memory.peakUsage = currentUsage;
        this.metrics.memory.memoryGrowth = currentUsage - this.metrics.memory.initialUsage;
        if (jsHeap) {
            this.metrics.memory.jsHeapSize = jsHeap.total;
            this.metrics.memory.usedJSHeapSize = jsHeap.used;
            this.metrics.memory.totalJSHeapSizeLimit = jsHeap.limit;
        }
    }
    updateJavaScriptMetrics() {
        const measurements = this.completedMeasurements.filter((m)=>void 0 !== m.duration);
        if (0 === measurements.length) return;
        let totalTime = 0;
        let slowestTime = 0;
        let slowestName = '';
        for (const measurement of measurements){
            const duration = measurement.duration;
            totalTime += duration;
            if (duration > slowestTime) {
                slowestTime = duration;
                slowestName = measurement.name;
            }
        }
        this.metrics.javascript.totalExecutionTime = totalTime;
        this.metrics.javascript.functionCallCount = measurements.length;
        this.metrics.javascript.averageFunctionTime = totalTime / measurements.length;
        this.metrics.javascript.slowestFunctionTime = slowestTime;
        this.metrics.javascript.slowestFunctionName = slowestName;
    }
    startFPSTracking() {
        let frames = 0;
        let lastTime = performance.now();
        let lastSecond = Math.floor(lastTime / 1000);
        const trackFrame = ()=>{
            if (!this.isActive) return;
            const now = performance.now();
            const currentSecond = Math.floor(now / 1000);
            frames++;
            this.metrics.rendering.frameCount++;
            if (currentSecond > lastSecond) {
                const fps = frames;
                this.metrics.rendering.fps = fps;
                if (fps < this.metrics.rendering.minFps) this.metrics.rendering.minFps = fps;
                if (fps > this.metrics.rendering.maxFps) this.metrics.rendering.maxFps = fps;
                const totalSeconds = currentSecond - Math.floor(this.startTimestamp / 1000);
                this.metrics.rendering.avgFps = this.metrics.rendering.frameCount / totalSeconds;
                this.metrics.rendering.avgFrameTime = 1000 / (this.metrics.rendering.avgFps || 1);
                frames = 0;
                lastSecond = currentSecond;
            }
            this.frameId = requestAnimationFrame(trackFrame);
        };
        this.frameId = requestAnimationFrame(trackFrame);
    }
    getCurrentMemoryUsage() {
        if ('memory' in performance) return performance.memory.usedJSHeapSize || 0;
        return this.estimateMemoryUsage();
    }
    getJSHeapSize() {
        if ('memory' in performance) {
            const memory = performance.memory;
            return {
                total: memory.totalJSHeapSize || 0,
                used: memory.usedJSHeapSize || 0,
                limit: memory.jsHeapSizeLimit || 0
            };
        }
    }
    estimateMemoryUsage() {
        const measurementSize = 200 * this.completedMeasurements.length;
        const activeSize = 150 * this.activeMeasurements.size;
        return measurementSize + activeSize;
    }
    startMeasurement(name, metadata = {}) {
        const id = `measure_${++this.measurementCounter}_${Date.now()}`;
        const measurement = {
            id,
            name,
            startTime: performance.now(),
            metadata
        };
        this.activeMeasurements.set(id, measurement);
        return id;
    }
    endMeasurement(id) {
        const measurement = this.activeMeasurements.get(id);
        if (!measurement) return;
        const endTime = performance.now();
        measurement.endTime = endTime;
        measurement.duration = endTime - measurement.startTime;
        this.activeMeasurements.delete(id);
        this.completedMeasurements.push(measurement);
        if (this.completedMeasurements.length > this.options.maxMeasurements) this.completedMeasurements.splice(0, this.completedMeasurements.length - this.options.maxMeasurements);
        return measurement;
    }
    recordCustomMetric(name, value) {
        this.metrics.custom[name] = value;
    }
    getMetrics() {
        return {
            ...this.metrics
        };
    }
    getActiveMeasurements() {
        return Array.from(this.activeMeasurements.values());
    }
    getCompletedMeasurements() {
        return [
            ...this.completedMeasurements
        ];
    }
    reset() {
        this.activeMeasurements.clear();
        this.completedMeasurements.length = 0;
        this.measurementCounter = 0;
        this.metrics = this.createInitialMetrics();
    }
    exportData() {
        return JSON.stringify({
            metrics: this.metrics,
            activeMeasurements: Array.from(this.activeMeasurements.values()),
            completedMeasurements: this.completedMeasurements,
            isActive: this.isActive,
            exportedAt: Date.now()
        }, null, 2);
    }
    importData(data) {
        try {
            const parsed = JSON.parse(data);
            if (parsed.metrics) this.metrics = parsed.metrics;
            if (parsed.completedMeasurements) this.completedMeasurements = parsed.completedMeasurements;
            if (parsed.activeMeasurements) {
                this.activeMeasurements.clear();
                for (const measurement of parsed.activeMeasurements)this.activeMeasurements.set(measurement.id, measurement);
            }
        } catch (error) {
            throw new Error(`Failed to import performance data: ${error}`);
        }
    }
    generateReport() {
        const summary = this.generateSummary();
        const breakdown = this.generateBreakdown();
        const issues = this.analyzeIssues();
        return {
            summary,
            breakdown,
            issues,
            rawData: this.getMetrics(),
            generatedAt: Date.now()
        };
    }
    generateSummary() {
        const metrics = this.metrics;
        let score = 100;
        if (metrics.memory.memoryGrowth > 52428800) score -= 20;
        if (metrics.rendering.avgFps < 30) score -= 25;
        else if (metrics.rendering.avgFps < 50) score -= 10;
        if (metrics.blocking.longestBlockingTime > 100) score -= 15;
        if (metrics.javascript.averageFunctionTime > 50) score -= 20;
        score = Math.max(0, Math.min(100, score));
        let primaryBottleneck = 'None detected';
        if (metrics.rendering.avgFps < 30) primaryBottleneck = 'Rendering performance';
        else if (metrics.blocking.longestBlockingTime > 100) primaryBottleneck = 'Main thread blocking';
        else if (metrics.javascript.averageFunctionTime > 50) primaryBottleneck = 'JavaScript execution';
        else if (metrics.memory.memoryGrowth > 52428800) primaryBottleneck = 'Memory usage';
        return {
            overallScore: score,
            primaryBottleneck,
            analysisDuration: metrics.totalTime,
            kpi: {
                avgResponseTime: metrics.javascript.averageFunctionTime,
                memoryEfficiency: Math.max(0, 100 - metrics.memory.memoryGrowth / 10485760 * 10),
                renderingPerformance: Math.min(100, metrics.rendering.avgFps / 60 * 100),
                mainThreadUtilization: Math.max(0, 100 - metrics.blocking.totalBlockingTime / metrics.totalTime * 100)
            }
        };
    }
    generateBreakdown() {
        const metrics = this.metrics;
        return {
            javascript: {
                score: metrics.javascript.averageFunctionTime < 10 ? 100 : metrics.javascript.averageFunctionTime < 25 ? 75 : metrics.javascript.averageFunctionTime < 50 ? 50 : 25,
                totalTime: metrics.javascript.totalExecutionTime,
                bottlenecks: metrics.javascript.slowestFunctionName ? [
                    metrics.javascript.slowestFunctionName
                ] : []
            },
            memory: {
                score: metrics.memory.memoryGrowth < 10485760 ? 100 : metrics.memory.memoryGrowth < 26214400 ? 75 : metrics.memory.memoryGrowth < 52428800 ? 50 : 25,
                efficiency: Math.max(0, 100 - metrics.memory.memoryGrowth / 52428800 * 100),
                leakRisk: metrics.memory.memoryGrowth > 104857600 ? 80 : 20
            },
            rendering: {
                score: metrics.rendering.avgFps >= 55 ? 100 : metrics.rendering.avgFps >= 45 ? 75 : metrics.rendering.avgFps >= 30 ? 50 : 25,
                smoothness: Math.min(100, metrics.rendering.avgFps / 60 * 100),
                frameDrops: metrics.rendering.droppedFrames
            },
            mainThread: {
                score: metrics.blocking.longestBlockingTime < 50 ? 100 : metrics.blocking.longestBlockingTime < 100 ? 75 : metrics.blocking.longestBlockingTime < 200 ? 50 : 25,
                utilization: Math.min(100, metrics.blocking.totalBlockingTime / metrics.totalTime * 100),
                blockingImpact: metrics.blocking.longTaskCount
            }
        };
    }
    analyzeIssues() {
        const issues = [];
        const metrics = this.metrics;
        if (metrics.memory.memoryGrowth > 104857600) issues.push({
            severity: 'critical',
            category: 'memory',
            title: 'Excessive Memory Growth',
            description: 'Application memory usage has grown significantly, indicating potential memory leaks.',
            recommendations: [
                'Review object lifecycle management',
                'Check for unreleased event listeners',
                'Implement proper cleanup in component destruction',
                'Use memory profiling tools to identify leaks'
            ],
            relatedMetrics: {
                memoryGrowth: metrics.memory.memoryGrowth,
                currentUsage: metrics.memory.currentUsage
            }
        });
        if (metrics.rendering.avgFps < 30) issues.push({
            severity: 'high',
            category: 'rendering',
            title: 'Low Frame Rate',
            description: 'Average frame rate is below 30 FPS, causing noticeable performance issues.',
            recommendations: [
                'Optimize rendering pipeline',
                'Reduce DOM manipulations',
                'Use requestAnimationFrame for animations',
                'Consider virtual scrolling for large lists'
            ],
            relatedMetrics: {
                avgFps: metrics.rendering.avgFps,
                minFps: metrics.rendering.minFps
            }
        });
        if (metrics.blocking.longestBlockingTime > 200) issues.push({
            severity: 'high',
            category: 'blocking',
            title: 'Long Main Thread Blocking',
            description: 'Main thread is being blocked for extended periods, causing UI freezes.',
            recommendations: [
                'Break up long-running tasks',
                'Use Web Workers for heavy computations',
                'Implement task scheduling with setTimeout',
                'Optimize synchronous operations'
            ],
            relatedMetrics: {
                longestBlockingTime: metrics.blocking.longestBlockingTime,
                longTaskCount: metrics.blocking.longTaskCount
            }
        });
        if (metrics.javascript.averageFunctionTime > 100) issues.push({
            severity: 'medium',
            category: "javascript",
            title: 'Slow Function Execution',
            description: 'Average function execution time is high, impacting overall performance.',
            recommendations: [
                'Profile and optimize slow functions',
                'Implement caching where appropriate',
                'Consider algorithmic improvements',
                'Use performance.mark() for detailed timing'
            ],
            relatedMetrics: {
                averageFunctionTime: metrics.javascript.averageFunctionTime,
                slowestFunctionTime: metrics.javascript.slowestFunctionTime
            }
        });
        return issues;
    }
    destroy() {
        this.stop();
        if (this.performanceObserver) {
            this.performanceObserver.disconnect();
            this.performanceObserver = void 0;
        }
        this.activeMeasurements.clear();
        this.completedMeasurements.length = 0;
    }
}
export { PerformanceTracker };
